// Core
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { NavLink, useLocation} from 'react-router-dom';
import {
  AbsoluteCenter,
  Box,
  Flex,
  Button,
  Divider,
  Grid,
  Image,
  Link,
  Text,
  useDisclosure,
  useColorModeValue,
  useColorMode,
  useBreakpointValue
} from '@chakra-ui/react';

// Assets
import logoImage from 'assets/img/logo.png';
import headerImage from 'assets/img/header-sign-up.png';

// Components
import SignUp from './SignUp';
import SignUpFinish from './SignUpFinish';
import { 
  StarsIcon,  
  SignUpGamesIcon,
  SignUpMembershipIcon,
  SignUpPetrolIcon,
  EmailIcon,
  PhoneIcon
} from 'components/Icons/Icons';
import Card from 'components/Card/Card';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';

function SelectSignUp({step, setStep}) {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const topLineDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
  const topLineWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const topLine = useColorModeValue(topLineWhite, topLineDark);
  const spanStyle = {
    color: '#45B4EA',
    fontWeight: 700
  };

  const handleType = (type) => {
    switch (type) {
      case 'free':
        setStep(2);
        break;
      case 'membership':
        window.location.href = 'https://lmctgiveaway.com/loyalty-membership';
        break;
      case 'petrol':
        window.location.href = 'https://lmctgiveaway.com/petrol-loyalty';
        break;
      default:
        break;
    }
  };

  const [newObject, setNewObject] = useState({});
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
    { step === 1 &&
      <Box w='100%' 
        textAlign="center" 
        overflow={ isMobile ? 'vidible' : 'scroll'}
        sx={{
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none', 
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        h="100%" 
        py="80px"
        zIndex="3">
        <Box position="relative" mb={{base: '50px', sm: '60px', md: '79px'}} display="flex" alignItems="center" justifyContent="center" zIndex="3">
          <NavLink to="/"><Image src={logoImage} alt='lmctplus image' maxWidth="184px"/></NavLink>
        </Box>

        <Box position="relative" textAlign="center" zIndex="3">
          <Text as="h1" variant="header-gradient" 
            fontSize={{base: '32px', sm: '40px', md: '48px'}}
            >
            Welcome to LMCT+</Text>
          <Box bg={topLine} left="50%" transform="translateX(-50%) rotate(180deg)" bottom="-2px" h="1px" position="absolute" w="20%" />
        </Box>

        <Flex 
            display="inline-flex"
            textAlign="center"
            gap="8px"
            m="8px auto"
            zIndex="3"
            position="relative"
            alignItems="center">
            <StarsIcon fontSize="24px" />
            <Box borderRadius={{base: '8px', md: '16px'}} p="4px 14px 4px 16px" background="white" zIndex="3">
              <Text as="h2" variant="header-gradient" pr="2px" fontSize={{base: '26px', sm: '32px', md: '40px'}}>Sign up</Text>
            </Box>
            <StarsIcon fontSize="24px" />
        </Flex>   

        <Box position="relative" m="-80px auto -180px auto">
          <Image src={headerImage} alt="LMCT Sign Up" m="0 auto" h={{base: '232px', lg: '370px'}} objectFit="contain" />
          <Box 
            bg={ isDark ? '#00071C' : '#dfe9fd'} 
            zIndex="1" filter="blur(60px)" opacity="0.5" 
            borderRadius="100%" w={{base: '100%'}} 
            top={ isMobile ? '-100%' : '-60%'}
            m="0 auto"
            h="370px" position="absolute" />
          { isDark && <Box bg="rgba(64, 155, 255, 0.82)" 
              zIndex="-1" position="absolute" opacity="0.6" 
              left="30%"
              transform="translate(-50%,0)"
              filter="blur(70px)" h="300px" w="200px" mt="0" 
              top="0"
              borderRadius="full" /> }
          { isDark && <Box bg="rgba(64, 155, 255, 0.82)" 
              zIndex="-1" position="absolute" opacity="0.6" 
              right="30%"
              transform="translate(50%,0)"
              filter="blur(70px)" h="300px" w="200px" mt="0" 
              top="0"
              borderRadius="full" /> }
        </Box>

        <Grid 
          templateColumns={{ sm: '1fr', lg: '1fr 1fr ' }} 
          gap="10px" 
          m={{ base: '130px auto 38px auto', lg: '38px auto'}}
          w={{base: '100%', lg: '730px'}} 
          alignItems="center">
          <Card 
            padding="0px" 
            textAlign="left"
            border="2px solid #2ABDFD"
            bg={ isDark ? '#0D234D' : 'linear-gradient(0deg, rgba(9, 155, 255, 0.10) 0%, rgba(9, 155, 255, 0.10) 100%), #FFF'}
            boxShadow="0 1px 22.4px 0 rgba(9, 155, 255, 0.60)"
            >
            <Box position="relative" overflow="hidden" padding="16px">
              <Text variant="title-lg" fontSize="24px" fontWeight="900" textTransform="uppercase">Membership</Text>
              <Text variant="text-md" mt="20px">
                Unlock the full platform experience.<br /><br />
                Enjoy <span style={spanStyle}>unlimited access</span> to discounts, entries into 
                every giveaway (cars, houses, cash), events, special rewards, 
                and private community access.<br /><br />
                Upgrade now and <span style={spanStyle}>enjoy everything without limits</span> .
              </Text>
              <Button zIndex="2" 
                onClick={() => handleType('membership')} 
                variant={ isDark ? 'button-lg-g' : 'button-lg'} 
                mt="24px" 
                leftIcon={<SignUpMembershipIcon fontSize="24px"/>}>
                Get Full Access
              </Button>

              { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1, top: '-247px' }} />
               : <LinesWhite className="abstract-lines" style={{ zIndex: 1, top: '-247px', opacity: 0.3 }} /> }
            </Box>
          </Card> 

          <Card padding="16px" textAlign="left">
            <Text variant="title-lg" fontSize="24px" fontWeight="900" textTransform="uppercase">Free Play</Text>
            <Text variant="text-md" mt="20px">
              Start for free. Upgrade anytime. <br />
              Create a free account and enjoy in-app games. <br />
              Upgrade to unlock all events and exclusive experiences.
            </Text>
            <Button 
              onClick={() => handleType('free')} 
              variant="secondary-inverted-lg" 
              mt="24px" 
              leftIcon={<SignUpGamesIcon fontSize="24px" />}>
              Play for Free
            </Button>
          </Card>   
        </Grid> 

        <Grid templateColumns={{ sm: '1fr', lg: '1fr 1fr ' }} 
          w={{base: '100%', md: '430px'}}
          justifyContent="center"
          m="0 auto"
          gap="52px" templateRows='1fr' textAlign="left" mt="76px">
          <Box>
            <Text variant="title-md" fontWeight="700" pb="14px">Contact Us</Text>
            <Flex gap="6px" pb="10px">
              <EmailIcon fontSize="16px" />
              <Text variant="text-sm"><Link href="mailto:support@lmctplus.com" style={{color: '#099BFF', fontSize: '14px'}}>support@lmctplus.com</Link></Text>
            </Flex>
            <Flex gap="6px">
              <PhoneIcon fontSize="16px" color={isDark ? 'white' : '#0E1633'} />
              <Text variant="text-sm"><Link href="tel:+61 1300 683 688" style={{fontSize: '14px'}}>+61 1300 683 688</Link></Text>
            </Flex>
          </Box>

          <Box>
            <Text variant="text-md" pb="11px"><Link href="/terms-and-conditions">Terms & Conditions</Link></Text>
            <Text variant="text-md" pb="11px"><Link href="/privacy-policy">Privacy Policy</Link></Text>
            <Text color={isDark ? '#9FABCB' : '#262D47'}>© {new Date().getFullYear()} LMCT+</Text>
          </Box>
        </Grid>  
      </Box>
    }
    { step === 2 && <SignUp setStep={setStep} newObject={newObject} setNewObject={setNewObject} onOpenFinish={onOpen} topLine={topLine}/> }
    { step === 3 && 
      <>
        <SignUpFinish 
          newObject={newObject}
          isOpen={isOpen} 
          onOpen={onOpen}
          isDark={isDark} 
          topLine={topLine}
        /> 
      </>
    }
    </>
  );
}

export default SelectSignUp;